import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  RefreshCw, 
  AlertTriangle, 
  Wifi, 
  WifiOff,
  Battery,
  BatteryLow,
  Thermometer,
  Droplets,
  Wind,
  Activity,
  UtensilsCrossed,
  MapPin,
  Bell,
  Check,
  X,
  ChevronDown,
  Settings,
  User
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  getApartments,
  getDevices,
  getOpenAlerts,
  getRecentEvents,
  acknowledgeAlert,
  resolveAlert
} from '../lib/supabase';
import { useTranslation } from '../contexts/TranslationContext';

// Status calculation logic
function calculateStatus(resident, alerts, events, device) {
  const residentAlerts = alerts.filter(a => a.resident_id === resident.id);
  const hasUrgentAlert = residentAlerts.some(a => a.severity === 'critical' || a.alert_type === 'fall');
  const hasWarningAlert = residentAlerts.length > 0;
  const isOffline = device?.status === 'offline';
  
  // Check for high CO2
  const latestIAQM = events.find(e => e.resident_id === resident.id && e.event_type === 'iaqm_reading');
  const highCO2 = latestIAQM?.data?.co2_ppm > 1200;
  
  // Check breakfast (fridge_open today)
  const today = new Date().toDateString();
  const breakfastToday = events.some(e => 
    e.resident_id === resident.id && 
    e.event_type === 'fridge_open' && 
    new Date(e.ts).toDateString() === today
  );
  
  if (hasUrgentAlert || isOffline) return 'URGENT';
  if (hasWarningAlert || highCO2) return 'WARNING';
  return 'OK';
}

function StatusPill({ status }) {
  const styles = {
    OK: 'bg-green-500/10 text-green-500 border-green-500/20',
    WARNING: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    URGENT: 'bg-red-500/10 text-red-500 border-red-500/20 animate-pulse'
  };
  
  return (
    <span className={cn(
      "px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border",
      styles[status]
    )} data-testid={`status-pill-${status.toLowerCase()}`}>
      {status}
    </span>
  );
}

function MetricTile({ icon: Icon, label, value, unit, warning }) {
  return (
    <div className={cn(
      "flex items-center gap-2 p-2 rounded-md bg-[#09090b]/50",
      warning && "ring-1 ring-orange-500/50"
    )}>
      <Icon className={cn("h-4 w-4", warning ? "text-orange-500" : "text-[#52525b]")} />
      <div>
        <div className="font-mono text-sm font-medium">{value}{unit}</div>
        <div className="text-[10px] text-[#52525b] uppercase">{label}</div>
      </div>
    </div>
  );
}

function AlertBadge({ type, onClick }) {
  const config = {
    fall: { label: 'FALL', className: 'bg-red-500/20 text-red-500 border-red-500/30' },
    medical: { label: 'MEDICAL', className: 'bg-orange-500/20 text-orange-500 border-orange-500/30' },
    device_offline: { label: 'OFFLINE', className: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30' },
    iaqm: { label: 'AIR', className: 'bg-purple-500/20 text-purple-500 border-purple-500/30' }
  };
  
  const { label, className } = config[type] || config.medical;
  
  return (
    <Badge 
      variant="outline" 
      className={cn("text-[10px] font-bold cursor-pointer", className)}
      onClick={onClick}
      data-testid={`alert-badge-${type}`}
    >
      {label}
    </Badge>
  );
}

function MapDialog({ open, onOpenChange, resident, timestamp }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#18181b] border-[#27272a] max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-500" />
            {resident?.first_name} {resident?.last_name} - Location
          </DialogTitle>
        </DialogHeader>
        <div className="relative">
          {/* Fake Map */}
          <div className="w-full h-[400px] bg-[#09090b] rounded-lg overflow-hidden relative">
            {/* Map background pattern */}
            <div className="absolute inset-0 opacity-20">
              <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#3f3f46" strokeWidth="1"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
              </svg>
            </div>
            
            {/* Fake streets */}
            <div className="absolute inset-0">
              <div className="absolute top-1/3 left-0 right-0 h-8 bg-[#27272a]/50" />
              <div className="absolute top-2/3 left-0 right-0 h-6 bg-[#27272a]/30" />
              <div className="absolute left-1/4 top-0 bottom-0 w-6 bg-[#27272a]/40" />
              <div className="absolute left-2/3 top-0 bottom-0 w-8 bg-[#27272a]/50" />
            </div>
            
            {/* Buildings */}
            <div className="absolute top-[15%] left-[10%] w-16 h-12 bg-[#3f3f46]/50 rounded" />
            <div className="absolute top-[20%] left-[35%] w-20 h-16 bg-[#3f3f46]/50 rounded" />
            <div className="absolute top-[50%] left-[15%] w-24 h-20 bg-[#3f3f46]/50 rounded" />
            <div className="absolute top-[45%] right-[20%] w-32 h-24 bg-[#3f3f46]/50 rounded" />
            <div className="absolute bottom-[20%] left-[40%] w-20 h-14 bg-[#3f3f46]/50 rounded" />
            
            {/* Residence building */}
            <div className="absolute top-[40%] left-[45%] w-12 h-12 bg-blue-500/30 rounded border-2 border-blue-500" />
            
            {/* Pin marker */}
            <div className="absolute top-[35%] left-[60%] transform -translate-x-1/2">
              <div className="relative">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg shadow-red-500/50 animate-bounce">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-red-500 rotate-45" />
              </div>
            </div>
            
            {/* Labels */}
            <div className="absolute bottom-4 left-4 text-xs text-[#52525b]">
              Eldera Residence Area
            </div>
            <div className="absolute top-4 right-4 bg-[#18181b]/80 px-2 py-1 rounded text-xs">
              <span className="text-red-500">●</span> {resident?.first_name} {resident?.last_name}
            </div>
          </div>
          
          {/* Location info */}
          <div className="mt-4 p-3 bg-[#09090b] rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Current Location</div>
                <div className="text-xs text-[#52525b] mt-1">Near: Park Area - 150m from residence</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[#52525b]">Last update</div>
                <div className="text-sm font-mono">{timestamp ? new Date(timestamp).toLocaleTimeString() : '--:--'}</div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ApartmentCard({ apartment, resident, device, alerts, events, onRefresh, onShowMap }) {
  const [resolving, setResolving] = useState(false);
  
  const status = calculateStatus(resident, alerts, events, device);
  const residentAlerts = alerts.filter(a => a.resident_id === resident.id);
  
  // Get latest metrics
  const latestActivity = events.find(e => e.resident_id === resident.id && e.event_type === 'activity_score');
  const latestIAQM = events.find(e => e.resident_id === resident.id && e.event_type === 'iaqm_reading');
  const latestOutside = events.find(e => e.resident_id === resident.id && e.event_type === 'tracker_outside');
  
  // Check breakfast
  const today = new Date().toDateString();
  const breakfastEvent = events.find(e => 
    e.resident_id === resident.id && 
    e.event_type === 'fridge_open' && 
    new Date(e.ts).toDateString() === today
  );
  
  const isOutside = latestOutside?.data?.outside;
  
  const handleAcknowledge = async (alertId) => {
    try {
      await acknowledgeAlert(alertId);
      toast.success('Alert acknowledged');
      onRefresh();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };
  
  const handleResolve = async (alertId, resolution) => {
    setResolving(true);
    try {
      await resolveAlert(alertId, resolution);
      toast.success('Alert resolved');
      onRefresh();
    } catch (error) {
      toast.error('Failed to resolve alert');
    } finally {
      setResolving(false);
    }
  };
  
  const hasFallAlert = residentAlerts.some(a => a.alert_type === 'fall');
  const firstOpenAlert = residentAlerts[0];

  return (
    <div 
      className={cn(
        "bg-[#18181b] border border-[#27272a] rounded-xl p-4 sm:p-5 transition-all duration-200 hover:border-blue-500/30 group relative overflow-hidden",
        status === 'URGENT' && "border-red-500/50 shadow-[0_0_15px_rgba(239,68,68,0.2)]"
      )}
      data-testid={`apartment-card-${apartment.apt_label}`}
    >
      {/* Status glow effect */}
      {status === 'URGENT' && (
        <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-transparent pointer-events-none" />
      )}
      
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-barlow font-bold text-lg text-[#fafafa]">{apartment.apt_label}</div>
          <Link 
            to={`/resident/${resident.id}`}
            className="text-sm text-[#a1a1aa] hover:text-blue-500 transition-colors flex items-center gap-1"
            data-testid={`resident-link-${resident.id}`}
          >
            <User className="h-3 w-3" />
            {resident.first_name} {resident.last_name}
          </Link>
        </div>
        <StatusPill status={status} />
      </div>
      
      {/* Alert Badges */}
      {residentAlerts.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {residentAlerts.map(alert => (
            <AlertBadge key={alert.id} type={alert.alert_type} />
          ))}
        </div>
      )}
      
      {/* Metrics Grid */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        {/* Activity Score */}
        <MetricTile 
          icon={Activity}
          label="Activity"
          value={latestActivity?.data?.score || '--'}
          unit=""
        />
        
        {/* Device Status */}
        <div className={cn(
          "flex items-center gap-2 p-2 rounded-md bg-[#09090b]/50",
          device?.status === 'offline' && "ring-1 ring-red-500/50"
        )}>
          {device?.status === 'online' ? (
            <Wifi className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-500" />
          )}
          <div>
            <div className="flex items-center gap-1">
              {device?.battery_percent > 20 ? (
                <Battery className="h-3 w-3 text-[#52525b]" />
              ) : (
                <BatteryLow className="h-3 w-3 text-orange-500" />
              )}
              <span className="font-mono text-sm">{device?.battery_percent || '--'}%</span>
            </div>
            <div className="text-[10px] text-[#52525b] uppercase">Bracelet</div>
          </div>
        </div>
      </div>
      
      {/* IAQM */}
      {latestIAQM?.data && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <MetricTile 
            icon={Thermometer}
            label="Temp"
            value={latestIAQM.data.temp_c?.toFixed(1) || '--'}
            unit="°C"
          />
          <MetricTile 
            icon={Droplets}
            label="Humidity"
            value={latestIAQM.data.hum_pct || '--'}
            unit="%"
          />
          <MetricTile 
            icon={Wind}
            label="CO2"
            value={latestIAQM.data.co2_ppm || '--'}
            unit="ppm"
            warning={latestIAQM.data.co2_ppm > 1200}
          />
        </div>
      )}
      
      {/* Status indicators */}
      <div className="flex items-center gap-3 text-xs mb-4">
        {/* Breakfast */}
        <div className={cn(
          "flex items-center gap-1.5 px-2 py-1 rounded-md",
          breakfastEvent ? "bg-green-500/10 text-green-500" : "bg-[#09090b]/50 text-[#52525b]"
        )}>
          <UtensilsCrossed className="h-3 w-3" />
          <span>{breakfastEvent ? 'Breakfast OK' : 'No breakfast'}</span>
        </div>
        
        {/* Outside - Clickable */}
        {isOutside && (
          <button
            onClick={() => onShowMap(resident, latestOutside?.ts)}
            className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors"
            data-testid={`outside-btn-${resident.id}`}
          >
            <MapPin className="h-3 w-3" />
            <span>Outside</span>
          </button>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#27272a]">
        <Link to={`/resident/${resident.id}`}>
          <Button variant="ghost" size="sm" data-testid={`view-resident-${resident.id}`} className="h-8 px-2 sm:px-3">
            View
          </Button>
        </Link>
        
        <Link to={`/settings/${resident.id}`}>
          <Button variant="ghost" size="sm" data-testid={`settings-resident-${resident.id}`} className="h-8 px-2">
            <Settings className="h-4 w-4" />
          </Button>
        </Link>
        
        {hasFallAlert && firstOpenAlert && (
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => handleAcknowledge(firstOpenAlert.id)}
            data-testid={`acknowledge-alert-${firstOpenAlert.id}`}
            className="h-8 px-2 sm:px-3 text-xs"
          >
            <Bell className="h-3.5 w-3.5 sm:mr-1" />
            <span className="hidden sm:inline">Acknowledge</span>
          </Button>
        )}
        
        {firstOpenAlert && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={resolving}
                data-testid={`resolve-dropdown-${firstOpenAlert.id}`}
                className="h-8 px-2 sm:px-3 text-xs"
              >
                Resolve
                <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#18181b] border-[#27272a]">
              <DropdownMenuItem 
                onClick={() => handleResolve(firstOpenAlert.id, 'resident_ok')}
                data-testid="resolve-resident-ok"
              >
                <Check className="h-4 w-4 mr-2 text-green-500" />
                Resident OK
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleResolve(firstOpenAlert.id, 'false_alarm')}
                data-testid="resolve-false-alarm"
              >
                <X className="h-4 w-4 mr-2 text-orange-500" />
                False Alarm
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleResolve(firstOpenAlert.id, 'real_incident')}
                data-testid="resolve-real-incident"
              >
                <AlertTriangle className="h-4 w-4 mr-2 text-red-500" />
                Real Incident
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [apartments, setApartments] = useState([]);
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [mapDialog, setMapDialog] = useState({ open: false, resident: null, timestamp: null });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [apartmentsData, devicesData, alertsData, eventsData] = await Promise.all([
        getApartments(),
        getDevices(),
        getOpenAlerts(),
        getRecentEvents()
      ]);
      
      setApartments(apartmentsData || []);
      setDevices(devicesData || []);
      setAlerts(alertsData || []);
      setEvents(eventsData || []);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError(err.message);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleShowMap = (resident, timestamp) => {
    setMapDialog({ open: true, resident, timestamp });
  };

  // Count online devices
  const onlineDevices = devices.filter(d => d.status === 'online').length;
  const openAlertsCount = alerts.length;

  return (
    <div className="p-4 sm:p-6 md:p-8 pt-16 sm:pt-4" data-testid="dashboard-page">
      {/* Top Bar */}
      <div className="flex flex-col gap-4 mb-6 md:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-barlow font-bold text-xl sm:text-2xl md:text-3xl tracking-tight">
              {t('residence_overview')}
            </h1>
            <p className="text-[#a1a1aa] text-sm mt-1">
              Demo Residence - 6 {t('apartments')}
            </p>
          </div>
          
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Stats */}
            <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <Wifi className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500" />
                <span className="font-mono">{onlineDevices}</span>
                <span className="text-[#52525b] hidden sm:inline">{t('devices_online')}</span>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <AlertTriangle className={cn(
                  "h-3.5 w-3.5 sm:h-4 sm:w-4",
                  openAlertsCount > 0 ? "text-red-500" : "text-[#52525b]"
                )} />
                <span className="font-mono">{openAlertsCount}</span>
                <span className="text-[#52525b] hidden sm:inline">{t('alerts')}</span>
              </div>
            </div>
            
            {/* Refresh */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-button"
              className="px-2 sm:px-3"
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              <span className="hidden sm:inline ml-2">Refresh</span>
            </Button>
          </div>
        </div>
        
        {/* Last updated */}
        <div className="text-xs text-[#52525b] font-mono">
          Last updated: {lastRefresh.toLocaleTimeString()}
        </div>
      </div>
      
      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6 text-red-500">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            <span>Error loading data: {error}</span>
          </div>
          <p className="text-sm mt-2 text-[#a1a1aa]">
            Check your Supabase credentials and ensure the database is accessible.
          </p>
        </div>
      )}
      
      {/* Loading State */}
      {loading && apartments.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-[#18181b] border border-[#27272a] rounded-xl p-5 h-[300px] animate-pulse">
              <div className="h-6 bg-[#27272a] rounded w-1/3 mb-3" />
              <div className="h-4 bg-[#27272a] rounded w-1/2 mb-6" />
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="h-16 bg-[#27272a] rounded" />
                <div className="h-16 bg-[#27272a] rounded" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="h-12 bg-[#27272a] rounded" />
                <div className="h-12 bg-[#27272a] rounded" />
                <div className="h-12 bg-[#27272a] rounded" />
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Apartment Grid */}
      {!loading || apartments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="apartments-grid">
          {apartments.map(apt => {
            const resident = apt.residents?.[0];
            if (!resident) return null;
            
            const device = devices.find(d => d.resident_id === resident.id);
            
            return (
              <ApartmentCard
                key={apt.id}
                apartment={apt}
                resident={resident}
                device={device}
                alerts={alerts}
                events={events}
                onRefresh={fetchData}
                onShowMap={handleShowMap}
              />
            );
          })}
          
          {/* Show placeholder cards if less than 6 apartments */}
          {apartments.length < 6 && apartments.length > 0 && [...Array(6 - apartments.length)].map((_, i) => (
            <div 
              key={`placeholder-${i}`}
              className="bg-[#18181b]/50 border border-[#27272a] border-dashed rounded-xl p-5 flex items-center justify-center text-[#52525b]"
            >
              <span className="text-sm">Empty apartment slot</span>
            </div>
          ))}
        </div>
      ) : null}
      
      {/* No data state */}
      {!loading && apartments.length === 0 && !error && (
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-[#52525b] mx-auto mb-4" />
          <h3 className="font-barlow font-bold text-lg mb-2">No Apartments Found</h3>
          <p className="text-[#a1a1aa] text-sm mb-4">
            No apartments found for the demo residence. Please check your Supabase database setup.
          </p>
          <Link to="/simulation">
            <Button data-testid="go-to-simulation">
              Go to Simulation
            </Button>
          </Link>
        </div>
      )}
      
      {/* Map Dialog */}
      <MapDialog 
        open={mapDialog.open}
        onOpenChange={(open) => setMapDialog({ ...mapDialog, open })}
        resident={mapDialog.resident}
        timestamp={mapDialog.timestamp}
      />
    </div>
  );
}
